import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftRight, ExternalLink, Check, X } from 'lucide-react';
import { SwapForm, SwapStatus } from './SwapForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-swap/admin`;
const FUNDING = '0x1877f2F7c31CCbB2B23EA400991a9B5d2CCC32AC';
const WALLETS = [{ id: 'treasury', label: 'Treasury wallet' }, { id: 'funding', label: `Funding wallet ${FUNDING.slice(0, 6)}…${FUNDING.slice(-4)}` }];
const n = (v, d = 2) => (v == null ? '-' : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

const WalletBalances = ({ bal }) => (
  <div className="text-sm text-slate-300 mb-4 space-y-1" data-testid="admin-swap-balances">
    <p className="font-mono text-xs text-slate-400" data-testid="admin-swap-wallet-address">{bal.wallet}</p>
    <p className="capitalize">{bal.market}: {n(bal.ETH, 6)} ETH · {n(bal.USDC)} USDC · {n(bal.BETH)} {bal.market === 'base' ? 'BETH USD' : 'Bethcoin (old)'}</p>
    {bal.gas_low && <p className="text-xs text-amber-300" data-testid="admin-swap-gas-low">This wallet has almost no ETH on {bal.market} for network fees. Send it a little ETH before swapping.</p>}
    {bal.can_sign === false && <p className="text-xs text-red-300" data-testid="admin-swap-cannot-sign">This wallet's private key isn't saved, so it can't swap.</p>}
  </div>
);

const RequestRow = ({ r, onAct }) => (
  <tr className="border-t border-slate-700" data-testid={`admin-swap-request-${r.id}`}>
    <td className="py-2 text-xs">{new Date(r.created_at).toLocaleString()}</td>
    <td className="text-xs">{r.admin_direct ? `Admin (${r.wallet?.toLowerCase() === FUNDING.toLowerCase() ? 'funding wallet' : 'treasury'})` : r.email}</td>
    <td>{r.amount} {r.token_in} → ~{Number(r.quote?.amount_out || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })} {r.token_out}</td>
    <td className="capitalize">{r.market}</td>
    <td><SwapStatus s={r.status} />{r.error && <p className="text-xs text-red-400 max-w-xs">{r.error}</p>}</td>
    <td className="whitespace-nowrap space-x-1">
      {r.status === 'pending' && <>
        <button onClick={() => onAct(r.id, 'approve')} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs" data-testid={`admin-swap-approve-${r.id}`}><Check className="w-3 h-3 inline" /> Approve</button>
        <button onClick={() => onAct(r.id, 'reject')} className="px-2 py-1 bg-slate-600 text-white rounded text-xs" data-testid={`admin-swap-reject-${r.id}`}><X className="w-3 h-3 inline" /> Reject</button>
      </>}
      {r.result?.explorer_url && <a href={r.result.explorer_url} target="_blank" rel="noreferrer" className="text-emerald-400 text-xs inline-flex items-center gap-1">tx <ExternalLink className="w-3 h-3" /></a>}
    </td>
  </tr>
);

export const AdminSwapPanel = ({ token }) => {
  const [reqs, setReqs] = useState([]);
  const [bal, setBal] = useState(null);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [pick, setPick] = useState({ market: 'base', wallet: 'treasury' });
  const load = useCallback(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/requests`, h).then(r => setReqs(r.data.requests)).catch(() => {});
    axios.get(`${API}/balances`, { ...h, params: pick }).then(r => setBal({ ...r.data, market: pick.market })).catch(() => {});
  }, [token, pick]);
  const onFormChange = (f) => { setQuote(null); if (f.market !== pick.market || f.wallet !== pick.wallet) setPick({ market: f.market, wallet: f.wallet }); };
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!reqs.some(r => r.status === 'executing')) return undefined;
    const t = setInterval(load, 6000); return () => clearInterval(t);
  }, [reqs, load]);

  const call = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } setBusy(false); };
  const getQuote = (b) => call(async () => { setQuote(null); setQuote((await axios.post(`${API}/quote`, b, auth)).data); });
  const swap = (b) => call(async () => { const r = await axios.post(`${API}/swap`, b, auth); toast.success(r.data.message); setQuote(null); load(); });
  const act = (id, kind) => call(async () => { const r = await axios.post(`${API}/requests/${id}/${kind}`, {}, auth); toast.success(r.data.message); load(); });

  return (
    <div className="space-y-6" data-testid="admin-swap-panel">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-1"><ArrowLeftRight className="w-5 h-5 text-cyan-400" /> Admin Swap (unrestricted)</h2>
          <p className="text-xs text-slate-400 mb-4">Runs immediately on Uniswap from the wallet you pick. Balances are checked before anything is sent.</p>
          {bal && <WalletBalances bal={bal} />}
          <SwapForm prefix="admin" onQuote={getQuote} onSubmit={swap} submitLabel="Swap now" busy={busy} quote={quote} wallets={WALLETS} onChange={onFormChange} />
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 text-sm text-slate-300 space-y-2">
          <h3 className="text-white font-semibold">How subscriber swaps work</h3>
          <p>Subscribers request a swap with a live Uniswap quote. Nothing moves until you approve it here.</p>
          <p>On approval the price is re-quoted and the swap runs from the subscriber's own wallet, protected by their max slippage. Gas is paid from their ETH.</p>
          <p>BETH swaps need a funded BETH/USDC Uniswap pool on that market.</p>
        </div>
      </div>
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="admin-swap-requests">
        <h3 className="text-white font-semibold mb-3">Swap requests ({reqs.filter(r => r.status === 'pending').length} waiting)</h3>
        {!reqs.length ? <p className="text-xs text-slate-500">No swaps yet.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm text-slate-300">
            <thead><tr className="text-left text-xs text-slate-500"><th>When</th><th>Who</th><th>Swap</th><th>Market</th><th>Status</th><th /></tr></thead>
            <tbody>{reqs.map(r => <RequestRow key={r.id} r={r} onAct={act} />)}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
};
